import { DayFrequency, ReminderFrequency, Reminders } from "@/types/reminders";
import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { formatMedicineTitle } from "./formatMedicine";
import { normalizeTo12HourTime } from "@/utils/date-utils";
import { DEFAULT_TIME_12HR } from "@/utils/constants";

const DAY_FREQUENCY_BY_SLOT_COUNT: DayFrequency[] = [
  DayFrequency.ONCE,
  DayFrequency.TWICE,
  DayFrequency.THRICE,
  DayFrequency.FOUR_TIMES,
];

// Converts a 24-hour "HH:mm" time (Pillbox's slot format) into the 12-hour
// "hh:mm AM/PM" string the Reminders module's time pickers expect — see
// DEFAULT_TIME_12HR / isTimeBefore in client/src/utils/date-utils.ts.
// Falls back to DEFAULT_TIME_12HR rather than passing an unparseable slot
// through into a time picker that would silently read it as midnight.
function to12HourTime(time24: string): string {
  return normalizeTo12HourTime(time24) ?? DEFAULT_TIME_12HR;
}

/**
 * Builds a partial Reminders object to prefill the Reminders module's "add
 * medication reminder" form (client/src/pages/reminders/medication) from an
 * existing active Pillbox medicine — used by the "Add Reminder" option on
 * the medicine detail page's options menu.
 *
 * Only meaningful for FIXED_SLOTS medicines: SOS/as-needed items have no
 * fixed daily slots, and every-N-hours dosing has no equivalent in the
 * Reminders module's frequency model. Returns null for either case so the
 * caller can hide "Add Reminder" instead of prefilling nonsense data.
 */
export function buildReminderPrefillFromPillboxItem(
  item: PillboxItem
): Partial<Reminders> | null {
  if (item.timingRuleType !== MedicineTimingRuleType.FIXED_SLOTS) return null;
  const slots = item.slots ?? [];
  if (slots.length === 0) return null;

  const dayFrequencyValue = slots.slice(0, 4).map(to12HourTime);
  const dayFrequency: DayFrequency =
    (item.dayFrequency as DayFrequency) ||
    DAY_FREQUENCY_BY_SLOT_COUNT[dayFrequencyValue.length - 1] ||
    DayFrequency.ONCE;

  const selectedWeekdays = item.selectedWeekdays ?? [];
  let frequency: ReminderFrequency;
  let frequencyValue: number | undefined;
  let selectedDays: number[] | undefined;

  if (selectedWeekdays.length > 0) {
    frequency = ReminderFrequency.SPECIFIC_DAYS;
    selectedDays = selectedWeekdays;
  } else {
    const interval = item.recurrenceIntervalDays ?? 1;
    if (interval <= 1) {
      frequency = ReminderFrequency.EVERY_DAY;
    } else if (interval === 2) {
      frequency = ReminderFrequency.EVERY_OTHER_DAY;
    } else {
      frequency = ReminderFrequency.DAY_FREQUENCY;
      frequencyValue = interval;
    }
  }

  return {
    // Links the created reminder back to this medicine (see
    // MedicineDetail.tsx, which looks reminders up by sourceId to show
    // "reminder already set" and to link into the edit flow).
    sourceId: item.id,
    reminderFor: formatMedicineTitle(item),
    frequency,
    frequencyValue,
    selectedDays,
    dayFrequency,
    dayFrequencyValue,
    // A fresh reminder starts today — Pillbox's own (possibly historical)
    // startDate isn't reused here since the Reminders date picker requires
    // today-or-later.
    startDate: new Date(),
    endDate: item.endDate ? new Date(item.endDate) : undefined,
    notes: item.notes ? item.notes.slice(0, 250) : undefined,
  };
}
